import {
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from "prop-types";
import { createContext, useContext, useState } from "react";

import styled from "styled-components/macro";

import { ctaTypes } from "Giving/constants";

import CampaignStatus from "./CTATypes/CampaignStatus";
import GiveToFund from "./CTATypes/GiveToFund";
import Message from "./CTATypes/Message";
import RecurringGift from "./CTATypes/RecurringGift";
import CTAWrapper from "./CTAWrapper";
import { useTypeAsString } from "./stateHooks";

const CTAContext = createContext();

export const useCTAContext = () => useContext(CTAContext);

/**
 * CTA component.
 *
 * @param {Object} props - Component props.
 * @param {number} props.type - The type of the CTA.
 * @param {string} props.action - The action to display on the CTA.
 * @param {string} props.icon - The icon to display on the CTA.
 * @param {string} props.color - The color scheme of the CTA.
 * @param {boolean} props.isDeleting - Whether the CTA is currently being deleted.
 * @param {string} props.deleteErrorMessage - The error message to display if the CTA deletion fails.
 * @param {string} props.title - The title of the CTA.
 * @param {string} props.description - The description of the CTA.
 * @param {string} props.fundName - The name of the fund associated with the CTA.
 * @param {number} props.fundID - The ID of the fund associated with the CTA.
 * @param {function} props.handleDelete - The function to call when the CTA is deleted.
 * @param {function} props.handleEdit - The function to call when the CTA is edited.
 * @param {boolean} props.newTab - if true, will take to the user to a new tab when selected.
 * @param {boolean} props.isPreview - if true, will display the CTA in preview mode (overrides hasMedia and growth considerations).
 * @param {boolean} props.fullWidth - if false, will display the CTA in mobile mode.
 * @param {string} props.link - suplies message type with the link to navigate to.
 * @param {number} props.suggestedRecurringGiftInterval
 * @param {number} props.suggestedAmount
 * @param {object} props.campaignInfo
 * @param {object} props.userPledgeInfo
 * @param {object} props.userRecurringGiftInfo
 * @returns {JSX.Element} The CTA component.
 */
export default function CTA({
  type,
  action,
  icon,
  color,
  isDeleting,
  deleteErrorMessage,
  handleDelete,
  handleEdit,
  moveForward,
  moveBackward,
  isPreview,
  fullWidth,
  newTab,
  targetFundID,
  link,
  suggestedRecurringGiftInterval,
  suggestedAmount,
  campaignInfo,
  userPledgeInfo,
  userRecurringGiftInfo,
  previewActionRef,
  fundDeleted,
  ...rest
}) {
  const [ctaAction, setCtaAction] = useState(action);
  const [isHovering, setIsHovering] = useState(false);
  const isAdmin = Boolean(
    typeof handleDelete === "function" && typeof handleEdit === "function"
  );
  const warningMessage = fundDeleted ? "Fund deleted" : null;
  return (
    <CTAContext.Provider
      value={{
        type,
        newTab,
        targetFundID,
        link,
        suggestedRecurringGiftInterval,
        suggestedAmount,
        campaignInfo,
        userPledgeInfo,
        userRecurringGiftInfo,
        isPreview,
        isHovering,
        setIsHovering,
        isAdmin,
        previewActionRef,
      }}
    >
      {/* Layout here is controlled from the outside by design. PreviewInfo is null when not a preview. */}
      <CTAWrapper
        action={ctaAction}
        icon={icon}
        color={color}
        isAdmin={isAdmin}
        isDeleting={isDeleting}
        deleteErrorMessage={deleteErrorMessage}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        moveForward={moveForward}
        moveBackward={moveBackward}
        fullWidth={fullWidth}
        isPreview={isPreview}
        warningMessage={warningMessage}
      >
        <InnerComponent
          type={type}
          action={ctaAction}
          setAction={setCtaAction}
          campaignInfo={campaignInfo}
          userPledgeInfo={userPledgeInfo}
          userRecurringGiftInfo={userRecurringGiftInfo}
          {...rest}
        />
      </CTAWrapper>
    </CTAContext.Provider>
  );
}
CTA.propTypes = {
  type: oneOfType([string, number]).isRequired,
  action: string,
  icon: string,
  color: string,
  isDeleting: bool,
  deleteErrorMessage: string,
  title: string,
  description: string,
  fundName: string,
  fundID: number,
  handleDelete: func,
  handleEdit: func,
  moveForward: func,
  moveBackward: func,
  newTab: bool,
  fullWidth: bool,
  isPreview: bool,
  targetFundID: number,
  link: string,
  suggestedRecurringGiftInterval: number,
  suggestedAmount: number,
  campaignInfo: object,
  userPledgeInfo: oneOfType([object, bool]),
  userRecurringGiftInfo: oneOfType([object, bool]),
  previewActionRef: object,
  fundDeleted: bool,
};

const InnerComponent = (props) => {
  const type = useTypeAsString();

  switch (type) {
    case ctaTypes.GIVE_TO_FUND:
      return <GiveToFund {...props} />;
    case ctaTypes.MESSAGE:
      return <Message {...props} />;
    case ctaTypes.RECURRING_GIFT:
      return <RecurringGift {...props} />;
    case ctaTypes.PLEDGE:
      return <CampaignStatus {...props} />;
    default:
      return null;
  }
};
